import { FunctionComponent, useEffect, useRef, useState } from 'react'
import { Box, Button, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import { toast } from 'react-toastify'
import axios from '../../utils/axios'
import colors from '../../colors'

/**
 * The organization logo.
 *
 * Org level rather than per membership: one logo is the Apple Wallet pass
 * icon, the header on transactional emails, the image on benefits, and the
 * fallback for any membership without its own. Apple requires an icon on
 * every pass, so wallet cards cannot be turned on until this is set.
 *
 * The server produces every Apple size from this upload, so the only thing
 * asked of the brewery is a large enough square-ish original.
 */

// Matches OrgLogoUploader exactly: the largest image the server produces is
// the 320x100 wallet logo, and anything at least that big downscales to
// every size without upscaling. Not an Apple rule - Apple specifies the
// outputs, not the source - so going higher only rejects logos that work.
const MIN_LONGEST_SIDE = 320
const MIN_SHORTEST_SIDE = 100
const MAX_BYTES = 2 * 1024 * 1024

const Branding: FunctionComponent = () => {
  const styles = useStyles()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [logoUrl, setLogoUrl] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Read live rather than from the cached user: that is a login-time snapshot
  // and would keep showing the previous logo after an upload.
  useEffect(() => {
    (async () => {
      try {
        const resp = await axios.get('/organization/current')
        setLogoUrl(resp.data.vipLogo ?? null)
      } catch {
        // Leave the empty state; uploading still works.
      }
    })()
  }, [])

  // Checked here as well as on the server so the brewery hears about a
  // too-small logo immediately rather than after a round trip.
  const measure = (file: File): Promise<{ width: number, height: number }> =>
    new Promise((resolve, reject) => {
      const img = new Image()
      const url = URL.createObjectURL(file)
      img.onload = () => {
        URL.revokeObjectURL(url)
        resolve({ width: img.naturalWidth, height: img.naturalHeight })
      }
      img.onerror = () => {
        URL.revokeObjectURL(url)
        reject(new Error('That file could not be read as an image.'))
      }
      img.src = url
    })

  const handleFile = async (file: File) => {
    setError(null)

    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setError('Logo must be a PNG or JPEG.')
      return
    }
    if (file.size > MAX_BYTES) {
      setError(`Logo must be under 2MB. Yours is ${(file.size / 1024 / 1024).toFixed(1)}MB.`)
      return
    }

    try {
      const { width, height } = await measure(file)
      const longest = Math.max(width, height)
      const shortest = Math.min(width, height)
      if (longest < MIN_LONGEST_SIDE || shortest < MIN_SHORTEST_SIDE) {
        setError(
          `Logo must be at least ${MIN_LONGEST_SIDE}px on its longest side and ` +
          `${MIN_SHORTEST_SIDE}px on its shortest, so it can be resized for a wallet ` +
          `pass without losing quality. Yours is ${width}×${height}.`
        )
        return
      }
    } catch (e: any) {
      setError(e.message)
      return
    }

    setSaving(true)
    try {
      const formData = new FormData()
      formData.append('logo_image', file)
      const resp = await axios.put('/organization/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setLogoUrl(resp.data.vipLogo ?? null)
      toast.success('Logo updated')
    } catch (e: any) {
      setError(e.response?.data?.message ?? 'Could not upload that logo. Please try again.')
    } finally {
      setSaving(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  return (
    <Box p={4}>
      <Typography variant="h5" gutterBottom>Branding</Typography>

      <div className={styles.lede}>
        Your logo appears on members' Apple Wallet cards, on your emails, and
        anywhere a membership has no image of its own.
      </div>

      <Box className={styles.row}>
        <div className={styles.preview}>
          {logoUrl
            ? <img src={logoUrl} alt="Organization logo" className={styles.previewImg} />
            : <span className={styles.previewEmpty}>No logo yet</span>}
        </div>

        <Box>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            disabled={saving}
            onClick={() => fileInputRef.current?.click()}
          >
            {saving ? 'Uploading…' : logoUrl ? 'Replace logo' : 'Upload logo'}
          </Button>

          <div className={styles.requirements}>
            PNG or JPEG · at least {MIN_LONGEST_SIDE}px on the longest side · under 2MB
          </div>
        </Box>
      </Box>

      {error && <div className={styles.error}>{error}</div>}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) handleFile(file)
        }}
        style={{ display: 'none' }}
      />
    </Box>
  )
}

const useStyles = makeStyles({
  lede: {
    fontSize: 14,
    color: colors.GREY_2,
    maxWidth: 560,
    lineHeight: 1.5,
    marginBottom: 24,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
  },
  preview: {
    width: 120,
    height: 120,
    flexShrink: 0,
    borderRadius: 12,
    border: `1px solid ${colors.GREY_5}`,
    background: colors.GREY_7,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  previewImg: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain' as const,
  },
  previewEmpty: {
    fontSize: 12,
    color: colors.GREY_4,
  },
  requirements: {
    fontSize: 12,
    color: colors.GREY_3,
    marginTop: 8,
    maxWidth: 360,
    lineHeight: 1.5,
  },
  error: {
    marginTop: 16,
    fontSize: 13,
    color: colors.DARK_RED,
    background: colors.PALE_RED,
    border: `1px solid ${colors.LIGHT_RED}`,
    borderRadius: 8,
    padding: '10px 12px',
    maxWidth: 560,
  },
})

export default Branding
